import { useCallback } from 'react';
import { TransferJobTaskStatus } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTaskStatus';
import type { AssetItem } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useFinderRefetch } from '@frameio/shared/contexts/FinderRefetchContext';
import { createOrUpdateTransferState } from '@frameio/transfers/utils';
import {
  filterUploadUrls,
  useUxpTransferMutations,
} from './useUxpTransferMutations';

export type AssetItems = (AssetItem & {
  project: {
    id: string;
  };
})[];

export const useCreateTransferAssets = () => {
  const activeAccountId = useActiveAccountId();
  const refetchFinder = useFinderRefetch();

  const {
    addAssetsToTransferBatch,
    createTransferBatch,
    createUXPTransferJob,
    startTransferJob,
    updateTransferBatch,
  } = useUxpTransferMutations();

  return useCallback(
    async function createTransferAssets({
      jobId,
      filePaths,
      fileNames,
      folderAssetId,
    }: {
      jobId: string;
      filePaths: string[];
      fileNames: string[];
      folderAssetId: string;
    }) {
      const { data: uxpTransferJobData } = await createUXPTransferJob({
        variables: {
          batchItemId: jobId,
          filePaths,
          originalFileNames: fileNames,
        },
      });

      const uxpTransferJob = uxpTransferJobData?.createUploadTransferJob;
      const assetsPayload = uxpTransferJob?.assets || [];

      const result = await createTransferBatch({
        variables: {
          input: {
            accountId: activeAccountId,
            name:
              fileNames.length > 1 ? `${fileNames.length} files` : fileNames[0],
            uploadedVia: 'DESKTOP',
            topLevelFileCount: assetsPayload.length,
          },
        },
      });
      const batchData = result?.data?.createTransferBatch?.transferBatch;
      if (!batchData?.id) throw new Error('Failed to create transfer batch');

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, addAssetsResult] = await Promise.all([
        updateTransferBatch({
          variables: {
            input: {
              id: batchData.id,
              status: 'PROCESSING',
            },
          },
        }),
        addAssetsToTransferBatch({
          variables: {
            input: {
              transferBatchId: batchData.id,
              assets: assetsPayload.map((assetPayload) => ({
                parentId: folderAssetId,
                id: assetPayload.id,
                name: assetPayload.name,
                type: assetPayload.type,
                filetype: assetPayload.fileType,
                filesize: assetPayload.filesize,
              })),
            },
          },
        }),
      ]);

      const assetItems =
        addAssetsResult?.data?.addAssetsToTransferBatch?.assetItems;

      assetItems?.forEach((assetItem, index) => {
        const assetId = assetItem.asset?.id;
        if (!assetId) return;
        createOrUpdateTransferState(assetId, {
          status: TransferJobTaskStatus.IDLE,
          assetId,
          jobId: uxpTransferJob?.id,
          filePath: filePaths[index],
        });
      });

      refetchFinder();

      const totalFileSize = uxpTransferJob?.assets.reduce(
        (acc, asset) => acc + (asset.filesize ?? 0),
        0
      );

      await startTransferJob({
        variables: {
          input: {
            jobId,
            assets:
              assetItems?.map((assetItem) => {
                return {
                  assetId: assetItem.asset!.id,
                  uploadUrls: filterUploadUrls(assetItem.asset!.uploadUrls),
                };
              }) ?? [],
          },
        },
      });

      return {
        jobId: uxpTransferJob?.id,
        totalFileSize,
        assetIds: assetItems?.map(({ asset }) => asset!.id) ?? [],
        transferBatchId: batchData.id,
        projectId: assetItems?.[0]?.asset?.project?.id ?? '',
      };
    },
    [
      activeAccountId,
      addAssetsToTransferBatch,
      createTransferBatch,
      createUXPTransferJob,
      refetchFinder,
      startTransferJob,
      updateTransferBatch,
    ]
  );
};
